import { format, getWeek, Locale } from "date-fns";
import { de, enGB } from "date-fns/locale";
import { DD_MM_YYYY } from "~/constants";
import { LocaleEnum } from "~/locales/resources";

/**
 * Check if today's date has passed the given payment due date.
 * @param paymentDueDate - The payment due date in the format "YYYY-MM-DD".
 * @returns true if today has passed the payment due date, otherwise false.
 */
export const hasPassedPaymentDueDate = (paymentDueDate: string | Date | undefined): boolean => {
  if (!paymentDueDate) return false;
  const today = new Date();
  const dueDate = new Date(paymentDueDate);

  // Set the time of today and dueDate to the start of the day for accurate comparison
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);

  return today > dueDate;
};

/**
 * Get the week number of the year
 * @param date
 * @returns Week number - Ex: 46
 */
export const getWeekNumber = (date: Date) => {
  return getWeek(date, {
    weekStartsOn: 1,
    firstWeekContainsDate: 4,
  });
};

/**
 * Formats a date object or string into a formatted date string.
 * @param date - The date object or string to format.
 * @param locale - The locale to use for formatting. Default is DE.
 * @param dateFormat - The date format to use. Default is DD_MM_YYYY.
 * @returns
 */
export const formatDate = (
  date: Date | string | undefined,
  locale?: LocaleEnum,
  dateFormat: string = DD_MM_YYYY
): string => {
  if (!date) return "";

  let dateObj: Date;

  if (typeof date === "string") {
    // When parsing an ISO date string without timezone information (e.g., "2025-11-11"),
    // JavaScript treats it as UTC. Adjust it to the local timezone for display purposes.
    const parsedDate = new Date(date);
    dateObj = new Date(parsedDate.getTime() + parsedDate.getTimezoneOffset() * 60_000);
  } else {
    // Clone the Date instance to avoid mutating the original object.
    dateObj = new Date(date.getTime());
  }

  const localeMap = {
    [LocaleEnum.EN]: enGB,
    [LocaleEnum.DE]: de,
  } as { [key in LocaleEnum]: Locale };

  return format(dateObj, dateFormat, {
    locale: localeMap[locale ?? LocaleEnum.DE],
  });
};

/**
 * Returns the formatted delivery period string based on the start and end dates.
 * If the years of the start and end dates are the same, only the end date's year is shown.
 * Otherwise, both the start and end dates include their years.
 *
 * @param startDate - The planned delivery window start date.
 * @param endDate - The planned delivery window end date.
 * @param locale - The locale to use for formatting.
 * @returns The formatted delivery period string.
 */
export const getFormattedDeliveryPeriod = (
  startDate?: Date | string,
  endDate?: Date | string,
  locale: LocaleEnum = LocaleEnum.DE
): string => {
  if (!startDate || !endDate) return "";

  const startYear = new Date(startDate).getFullYear();
  const endYear = new Date(endDate).getFullYear();

  // If start year and end year are the same, only display the year once
  if (startYear === endYear) {
    return formatDate(startDate, locale, "dd.MM") + " ~ " + formatDate(endDate, locale, "dd.MM.yyyy");
  }

  // If start year and end year are different, display both years
  return formatDate(startDate, locale, "dd.MM.yyyy") + " ~ " + formatDate(endDate, locale, "dd.MM.yyyy");
};

/**
 * Format date in German format (DD.MM.YYYY)
 * @param dateString - Date string to format
 * @returns Formatted date string in DD.MM.YYYY format or "-" if invalid
 */
export const formatGermanDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch {
    return "-";
  }
};

/**
 * Format Date object to YYYY-MM-DD string (without time/timezone)
 * @param date - Date object to format
 * @returns Formatted date string in YYYY-MM-DD format
 */
export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parse YYYY-MM-DD string to Date in local timezone
 */
export const parseDateFromYYYYMMDD = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const parts = dateStr.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  const [year, month, day] = parts;
  const date = new Date(year, month - 1, day);
  if (isNaN(date.getTime())) return null;
  return date;
};
